
import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MarketingPreviewSection } from '@/components/MarketingPreviewSection';
import { SEOHead } from '@/components/SEOHead';
import { getPlannerPageSeo } from '@/utils/calendarYearSeo';
import { plannerAnnualCalendarYear } from '@/lib/calendarYear';
import { MilkyWayCalendar as CalendarComponent } from '@/components/MilkyWayCalendar';
import { CreateAnnualCalendarModal } from '@/components/CreateAnnualCalendarModal';
import { useAuth } from '@/contexts/AuthContext';
import { LocationBrowser } from '@/components/locations/LocationBrowser';
import { getLocationBySlug, type Location } from '@/lib/locations';
import { useNewsletterGate } from '@/hooks/useNewsletterGate';
import { NewsletterGateModal } from '@/components/NewsletterGateModal';
import { NewsletterSignup } from '@/components/NewsletterSignup';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { LocationInput } from '@/components/LocationInput';
import {
  Calendar,
  MapPin,
  Clock,
  Crown,
  FileText,
  Star,
  ArrowRight,
  Search,
  Camera
} from 'lucide-react';
import { NearbyWorkshopsBanner } from '@/components/workshops/NearbyWorkshopsBanner';
import { trackPlannerRun } from '@/lib/analytics';
import { NearbyWorkshops } from '@/components/workshops/NearbyWorkshops';

interface SelectedLocation {
  id: string;
  name: string;
  latitude: number;
  longitude: number;
}

const MilkyWayCalendar: React.FC = () => {
  const { user, isSubscribed } = useAuth();
  const navigate = useNavigate();
  const [selectedLocation, setSelectedLocation] = useState<SelectedLocation | null>(null);
  const [activeMonth, setActiveMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [showAnnualCalendarModal, setShowAnnualCalendarModal] = useState(false);
  const [creatingCalendar, setCreatingCalendar] = useState(false);
  const [plannerTab, setPlannerTab] = useState<'custom' | 'darksky'>('custom');
  const calendarRef = useRef<HTMLDivElement>(null);

  const [searchParams] = useSearchParams();

  const {
    showGate,
    incrementViewCount,
    markAsSubscribed,
    closeGate
  } = useNewsletterGate();

  // Auto-select location from URL params
  // Supports ?location=slug (location snapshot) or ?name=...&lat=...&lon=... (custom)
  useEffect(() => {
    if (selectedLocation) return;
    const locationParam = searchParams.get('location');
    if (locationParam) {
      getLocationBySlug(locationParam).then((loc) => {
        if (loc) {
          handleLocationSelect({
            id: loc.id,
            name: loc.name,
            latitude: loc.latitude,
            longitude: loc.longitude,
          }, 'catalog');
        }
      }).catch(() => {});
      return;
    }
    const name = searchParams.get('name');
    const lat = searchParams.get('lat');
    const lon = searchParams.get('lon');
    if (name && lat && lon) {
      handleLocationSelect({
        id: 'custom',
        name: decodeURIComponent(name),
        latitude: parseFloat(lat),
        longitude: parseFloat(lon),
      }, 'custom');
    }
  }, [searchParams]);

  const availableYears = [2025, 2026, 2027, 2028, 2029];

  const handleLocationSelect = (location: SelectedLocation, locationType: 'custom' | 'catalog') => {
    setSelectedLocation(location);
    trackPlannerRun(locationType, location.id === 'custom' ? location.name : location.id);
    setTimeout(() => {
      calendarRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const handleCreateAnnualCalendar = async () => {
    if (!selectedLocation) return;

    if (isSubscribed && user?.email) {
      setCreatingCalendar(true);
      try {
        const { data, error } = await supabase.functions.invoke('create-shared-calendar', {
          body: {
            email: user.email,
            location_name: selectedLocation.name,
            latitude: selectedLocation.latitude,
            longitude: selectedLocation.longitude,
            year: selectedYear,
            user_id: user.id,
            is_premium: true,
            subscribe_newsletter: false,
          },
        });

        if (error) throw error;
        if (data?.slug) {
          toast.success('Annual calendar created!');
          navigate(`/calendar/${data.slug}`);
        }
      } catch (error) {
        console.error('Error creating calendar:', error);
        toast.error('Failed to create calendar');
      } finally {
        setCreatingCalendar(false);
      }
    } else {
      setShowAnnualCalendarModal(true);
    }
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const handlePremiumUpgrade = () => {
    window.location.href = '/register';
  };

  const handleBrowseLocationSelect = (loc: Location) => {
    handleLocationSelect({
      id: loc.id,
      name: loc.name,
      latitude: loc.latitude,
      longitude: loc.longitude,
    }, 'catalog');
  };

  const plannerSeo = getPlannerPageSeo();

  return (
    <>
      <SEOHead
        title={plannerSeo.title}
        description={plannerSeo.description}
        keywords={plannerSeo.keywords}
        canonicalUrl="/planner"
      />
      <div className="min-h-screen bg-background">

        {/* Hero — dark navy full-width */}
        <section className="bg-slate-950 text-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 py-16 md:py-20">
            <div className="text-center mb-10">
              <span className="eyebrow !text-gold-light mb-4 block">Planner</span>
              <h1 className="text-3xl md:text-4xl text-white tracking-[-0.03em] mb-4">
                Plan Your Milky Way Photography
              </h1>
              <p className="text-lg text-white/60 leading-relaxed max-w-2xl mx-auto mb-6">
                See the best nights to shoot — with galactic core timing, moon phases, and nightly ratings for any location.
              </p>
              <Link
                to={`/milky-way-calendar/${plannerAnnualCalendarYear()}`}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-[4px] bg-white/10 hover:bg-white/15 border border-white/20 text-white/70 hover:text-white text-sm transition-colors"
              >
                <Calendar className="w-4 h-4 text-gold" />
                See the {plannerAnnualCalendarYear()} annual calendar — all 30+ locations at a glance
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>

            {/* Step 1 label */}
            <p className="text-center text-sm text-white/40 uppercase tracking-wider font-medium mb-4">
              Step 1 — Choose a location
            </p>

            {/* Tabs */}
            <div className="max-w-xl mx-auto">
              <div className="flex rounded-lg overflow-hidden border border-white/20 mb-6">
                <button
                  onClick={() => setPlannerTab('custom')}
                  className={cn(
                    'flex-1 flex items-center justify-center gap-2 px-4 py-3.5 text-sm font-medium transition-colors',
                    plannerTab === 'custom'
                      ? 'bg-white/15 text-white'
                      : 'bg-white/5 text-white/50 hover:text-white/70 hover:bg-white/8'
                  )}
                >
                  <Search className="w-4 h-4" />
                  Search Any Location
                </button>
                <button
                  onClick={() => setPlannerTab('darksky')}
                  className={cn(
                    'flex-1 flex items-center justify-center gap-2 px-4 py-3.5 text-sm font-medium transition-colors',
                    plannerTab === 'darksky'
                      ? 'bg-white/15 text-white'
                      : 'bg-white/5 text-white/50 hover:text-white/70 hover:bg-white/8'
                  )}
                >
                  <MapPin className="w-4 h-4" />
                  Pick a Dark Sky Site
                </button>
              </div>

              {/* Custom location search */}
              {plannerTab === 'custom' && (
                <div className="bg-white/10 backdrop-blur-md border border-white/20 p-6 sm:p-8 rounded-lg [&_.text-muted-foreground]:text-white/75">
                  <p className="text-white/70 mb-4">Type a city, national park, or any place name — we'll find the coordinates.</p>
                  <LocationInput
                    variant="dark"
                    onLocationChange={(loc) => {
                      if (loc) {
                        handleLocationSelect({
                          id: 'custom',
                          name: loc.name,
                          latitude: loc.latitude,
                          longitude: loc.longitude,
                        }, 'custom');
                      }
                    }}
                  />
                  <p className="text-xs text-white/40 mt-3">Your calendar will appear below once you select a location.</p>
                </div>
              )}

              {/* Dark sky tab hint */}
              {plannerTab === 'darksky' && (
                <div className="bg-white/10 backdrop-blur-md border border-white/20 p-6 rounded-lg text-center">
                  <p className="text-white/70">Browse 400+ verified dark sky locations below to generate your calendar.</p>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Dark Sky Locations — only shown when tab is active */}
        {plannerTab === 'darksky' && (
          <section className="py-12">
            <div className="max-w-6xl mx-auto px-4 sm:px-6">
              <LocationBrowser
                onLocationSelect={(loc) => navigate(`/dark-sky-locations/${loc.slug}`)}
                showMap={true}
              />
            </div>
          </section>
        )}

        {/* Marketing Preview Section */}
        {selectedLocation && (!user || !isSubscribed) && (
          <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-12 pb-4">
            <MarketingPreviewSection onUpgradeClick={handlePremiumUpgrade} />
          </div>
        )}

        {/* Calendar Display */}
        {selectedLocation && (
          <div ref={calendarRef} className="scroll-mt-4 max-w-6xl mx-auto px-4 sm:px-6 py-12">
            {/* Calendar header */}
            <div className="border-b border-border pb-6 mb-6">
              <div className="flex items-start justify-between flex-wrap gap-4">
                <div>
                  <span className="eyebrow text-gold-foreground mb-2 block">Calendar Preview</span>
                  <div className="flex items-center gap-3 mb-1">
                    <h2 className="text-2xl text-foreground">
                      {selectedLocation.name}
                    </h2>
                    <Select value={selectedYear.toString()} onValueChange={(value) => setSelectedYear(parseInt(value))}>
                      <SelectTrigger className="w-28 h-9 text-sm font-medium border-border">
                        <Calendar className="w-3.5 h-3.5 mr-1.5 text-gold" />
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-popover z-50">
                        {availableYears.map((year) => (
                          <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
                    <span>{selectedLocation.latitude.toFixed(4)}°, {selectedLocation.longitude.toFixed(4)}°</span>
                    <span className="flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5" />
                      Local time
                    </span>
                  </div>
                </div>
                <Button
                  onClick={handleCreateAnnualCalendar}
                  disabled={creatingCalendar}
                  size="sm"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  {creatingCalendar ? 'Creating...' : 'Create Annual Calendar'}
                </Button>
              </div>
            </div>

            {/* Nearby Workshops Banner */}
            <div className="mb-6">
              <NearbyWorkshopsBanner
                latitude={selectedLocation.latitude}
                longitude={selectedLocation.longitude}
                locationName={selectedLocation.name}
              />
            </div>

            {/* Month tabs + calendar */}
            <Tabs value={activeMonth.toString()} onValueChange={(value) => setActiveMonth(parseInt(value))}>
              <TabsList className="grid grid-cols-6 lg:grid-cols-12 gap-1 h-auto p-1 mb-6">
                {monthNames.map((month, index) => (
                  <TabsTrigger
                    key={index}
                    value={index.toString()}
                    className="text-xs px-2 py-1.5 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
                  >
                    {month.slice(0, 3)}
                  </TabsTrigger>
                ))}
              </TabsList>

              {monthNames.map((month, index) => (
                <TabsContent key={index} value={index.toString()} className="mt-0">
                  <CalendarComponent
                    location={{
                      name: selectedLocation.name,
                      latitude: selectedLocation.latitude,
                      longitude: selectedLocation.longitude
                    }}
                    month={index}
                    year={selectedYear}
                    isYearlyView={true}
                    isMarketingPreview={true}
                    onViewChange={incrementViewCount}
                  />
                </TabsContent>
              ))}
            </Tabs>
          </div>
        )}

        {/* Nearby Workshop Cards */}
        {selectedLocation && (
          <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-8">
            <NearbyWorkshops
              latitude={selectedLocation.latitude}
              longitude={selectedLocation.longitude}
              locationName={selectedLocation.name}
            />
          </div>
        )}

        {/* Premium Upgrade CTA */}
        {(!user || !isSubscribed) && (
          <div className="border-y border-border">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 flex flex-col md:flex-row md:items-center md:justify-start md:gap-12 gap-4">
              <div>
                <h3 className="text-lg text-foreground mb-1 font-sans font-semibold">
                  Go Premium
                </h3>
                <ul className="text-sm text-muted-foreground space-y-0.5">
                  <li className="flex items-center gap-2">
                    <span className="text-gold">✦</span>
                    Moon Planner and Trip Planner
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-gold">✦</span>
                    PDF, Excel, CSV, and calendar exports
                  </li>
                </ul>
              </div>
              <Button
                variant="premium"
                onClick={handlePremiumUpgrade}
                size="lg"
                className="shrink-0"
              >
                <Crown className="w-4 h-4 mr-2" />
                Get Premium — $25/year
              </Button>
            </div>
          </div>
        )}

        {/* Workshops CTA */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
          <div className="p-6 sm:p-8 rounded-xl bg-[#0f1923] border border-white/10">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-lg bg-gold/10 flex items-center justify-center">
                    <Camera className="w-5 h-5 text-gold" />
                  </div>
                  <h3 className="text-lg font-semibold text-white">Learn from the Pros</h3>
                </div>
                <p className="text-sm text-white/70 leading-relaxed max-w-lg">
                  Browse astrophotography workshops at dark sky locations worldwide. Hands-on instruction from experienced photographers.
                </p>
              </div>
              <div className="flex gap-3 flex-shrink-0">
                <Link
                  to="/workshops"
                  className="inline-flex items-center gap-2 px-5 py-3 border border-gold text-gold hover:bg-gold hover:text-slate-950 font-semibold rounded-lg transition-colors text-sm"
                >
                  <Camera className="w-4 h-4" />
                  Browse Workshops
                </Link>
                <Link
                  to="/workshops/instructors"
                  className="inline-flex items-center gap-2 px-5 py-3 border border-white/20 text-white/70 hover:text-white hover:border-white/40 font-semibold rounded-lg transition-colors text-sm"
                >
                  Instructors
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Newsletter Signup */}
        <NewsletterSignup className="max-w-full" />

      </div>

      <NewsletterGateModal
        open={showGate}
        onClose={closeGate}
        onSuccess={markAsSubscribed}
      />

      {selectedLocation && (
        <CreateAnnualCalendarModal
          location={{
            name: selectedLocation.name,
            latitude: selectedLocation.latitude,
            longitude: selectedLocation.longitude
          }}
          selectedYear={selectedYear}
          isOpen={showAnnualCalendarModal}
          onClose={() => setShowAnnualCalendarModal(false)}
        />
      )}
    </>
  );
};

export default MilkyWayCalendar;
